import {
  createContext,
  useContext,
  useRef,
  useEffect,
  ReactNode,
  useCallback,
} from 'react';

interface TimeoutContextType {
  set: (id: string, callback: () => void, delay: number) => void;
  clear: (id: string) => void;
  clearAll: () => void;
}

const TimeoutContext = createContext<TimeoutContextType>(null!);

export const TimeoutProvider = ({ children }: { children: ReactNode }) => {
  const timeouts = useRef<Map<string, NodeJS.Timeout>>(new Map());

  const clear = useCallback((id: string) => {
    const timeout = timeouts.current.get(id);
    if (timeout) {
      clearTimeout(timeout);
      timeouts.current.delete(id);
    }
  }, []);

  const set = useCallback((id: string, callback: () => void, delay: number) => {
    clear(id);
    timeouts.current.set(id, setTimeout(callback, delay));
  },
    [clear],
  );

  const clearAll = useCallback(() => {
    timeouts.current.forEach(timeout => clearTimeout(timeout));
    timeouts.current.clear();
  }, []);

  useEffect(() => {
    return clearAll;
  }, [clearAll]);

  return (
    <TimeoutContext.Provider value={{ set, clear, clearAll }}>
      {children}
    </TimeoutContext.Provider>
  );
};

export const useTimeout = () => {
  const context = useContext(TimeoutContext);
  if (!context) {
    throw new Error('useTimeout must be used within a TimeoutProvider');
  }
  return context;
};
